import type { ChangeEvent, FC, FormEvent, ReactNode } from 'react'
import type { InquiryPayload } from '@/types'

import { useRouter } from 'next/router'
import { useEffect, useState } from 'react'

import {
    CircleAlert,
    CircleCheck,
    ChevronDown,
    LoaderCircle,
    RotateCcw,
    Send
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { buttonClasses } from '@/components/generals/button'
import { WhatsAppIcon } from '@/components/generals/icons'
import { budgetOptions, guestOptions } from '@/constants/content'
import { contactInfo, whatsappLink } from '@/constants/site'

type ServiceOption = {
    id: string
    slug: string
    title: string
}

interface InquiryFormProps {
    services: ServiceOption[]
}

type Status = 'idle' | 'submitting' | 'success' | 'error'
type FieldErrors = Partial<Record<keyof InquiryPayload, string>>

const OTHER_EVENT = 'Something else'

const emptyForm: InquiryPayload = {
    name: '',
    phone: '',
    email: '',
    eventType: '',
    eventDate: '',
    city: '',
    guests: '',
    budget: '',
    message: '',
    source: 'Contact page',
    company: ''
}

const inputClasses = (invalid: boolean): string => cn(
    'w-full rounded-xl border bg-white px-4 text-[0.95rem] text-ink outline-none transition placeholder:text-ink/35 focus:border-crimson focus:ring-4 focus:ring-crimson/10',
    invalid ? 'border-destructive' : 'border-input'
)

const selectClasses = cn(inputClasses(false), 'h-12 cursor-pointer appearance-none pr-11')

const labelClasses = 'mb-2 block text-sm font-semibold text-ink'

const SelectChevron: FC = (): ReactNode => {
    return (
        <ChevronDown aria-hidden='true' className='pointer-events-none absolute top-1/2 right-4 size-4 -translate-y-1/2 text-muted-foreground' />
    )
}

const validate = (form: InquiryPayload): FieldErrors => {
    const errors: FieldErrors = {}
    const digits = form.phone.replace(/\D/g, '')

    if (form.name.trim().length < 2) errors.name = 'Please enter your name.'
    if (digits.length < 10 || digits.length > 15) errors.phone = 'Please enter a valid phone number.'
    if (form.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) errors.email = 'Please enter a valid email address.'

    return errors
}

const whatsappSummary = (form: InquiryPayload): string => {
    const lines = [
        "Hi Guju Events, I'd like to plan an event.",
        form.name && `Name: ${form.name}`,
        form.eventType && `Event: ${form.eventType}`,
        form.eventDate && `Date: ${form.eventDate}`,
        form.city && `City or venue: ${form.city}`,
        form.guests && `Guests: ${form.guests}`,
        form.budget && `Budget: ${form.budget}`,
        form.message && `Details: ${form.message}`
    ]

    return lines.filter(Boolean).join('\n')
}

export const InquiryForm: FC<InquiryFormProps> = ({ services }): ReactNode => {
    const router = useRouter()
    const [form, setForm] = useState<InquiryPayload>(emptyForm)
    const [errors, setErrors] = useState<FieldErrors>({})
    const [status, setStatus] = useState<Status>('idle')
    const [serverMessage, setServerMessage] = useState('')
    const [minDate, setMinDate] = useState<string | undefined>(undefined)

    useEffect(() => {
        const today = new Date()
        const offset = today.getTimezoneOffset() * 60000
        setMinDate(new Date(today.getTime() - offset).toISOString().slice(0, 10))
    }, [])

    useEffect(() => {
        if (!router.isReady) return

        const requested = typeof router.query.service === 'string' ? router.query.service : ''
        const match = services.find((service) => service.slug === requested)

        if (match) setForm((current) => ({ ...current, eventType: match.title }))
    }, [router.isReady, router.query.service, services])

    const update = (field: keyof InquiryPayload) => (event: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>): void => {
        const value = event.target.value
        setForm((current) => ({ ...current, [field]: value }))

        if (errors[field]) {
            setErrors((current) => ({ ...current, [field]: undefined }))
        }
    }

    const onSubmit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
        event.preventDefault()

        const found = validate(form)
        setErrors(found)

        if (Object.keys(found).length > 0) {
            const firstField = Object.keys(found)[0]
            document.getElementById(`inquiry-${firstField}`)?.focus()
            return
        }

        setStatus('submitting')
        setServerMessage('')

        try {
            const response = await fetch('/api/inquiries', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(form)
            })

            const result = await response.json().catch(() => null) as { ok?: boolean, message?: string, fields?: FieldErrors } | null

            if (!response.ok || !result?.ok) {
                if (result?.fields) setErrors(result.fields)
                setServerMessage(result?.message ?? 'Something went wrong while sending your inquiry.')
                setStatus('error')
                return
            }

            setStatus('success')
        }
        catch {
            setServerMessage('We could not reach our server. Please check your connection, or call or WhatsApp us.')
            setStatus('error')
        }
    }

    const reset = (): void => {
        setForm(emptyForm)
        setErrors({})
        setServerMessage('')
        setStatus('idle')
    }

    if (status === 'success') {
        return (
            <div role='status' className='flex flex-col items-center py-10 text-center animate-in fade-in zoom-in-95 duration-500'>
                <span className='inline-flex size-16 items-center justify-center rounded-full bg-whatsapp/10 text-whatsapp'>
                    <CircleCheck className='size-8' aria-hidden='true' />
                </span>

                <h3 className='mt-6 font-heading text-4xl text-ink'>
                    Thank you,
                    {' '}
                    {form.name.trim().split(' ')[0]}
                </h3>

                <p className='mt-3 max-w-md text-muted-foreground'>
                    We have received your inquiry and our team will get back to you soon. Want to talk sooner? Continue the conversation on WhatsApp.
                </p>

                <div className='mt-8 flex w-full flex-col justify-center gap-3 sm:w-auto sm:flex-row'>
                    <a
                        href={whatsappLink(whatsappSummary(form))}
                        target='_blank'
                        rel='noopener noreferrer'
                        className={buttonClasses('whatsapp', 'lg')}
                    >
                        <WhatsAppIcon className='size-4' />
                        Continue on WhatsApp
                    </a>

                    <button type='button' onClick={reset} className={buttonClasses('outline-dark', 'lg')}>
                        <RotateCcw className='size-4' aria-hidden='true' />
                        Send another
                    </button>
                </div>
            </div>
        )
    }

    const submitting = status === 'submitting'

    return (
        <form onSubmit={onSubmit} noValidate aria-describedby='inquiry-note' className='relative grid gap-5 sm:grid-cols-2'>
            <div className='sm:col-span-2'>
                <label htmlFor='inquiry-name' className={labelClasses}>
                    Your name
                    <span className='text-crimson'> *</span>
                </label>
                <input
                    id='inquiry-name'
                    name='name'
                    type='text'
                    autoComplete='name'
                    required
                    value={form.name}
                    onChange={update('name')}
                    aria-invalid={Boolean(errors.name)}
                    aria-describedby={errors.name ? 'inquiry-name-error' : undefined}
                    placeholder='Full name'
                    className={cn(inputClasses(Boolean(errors.name)), 'h-12')}
                />
                {errors.name && <p id='inquiry-name-error' className='mt-2 text-sm text-destructive'>{errors.name}</p>}
            </div>

            <div>
                <label htmlFor='inquiry-phone' className={labelClasses}>
                    Phone or WhatsApp
                    <span className='text-crimson'> *</span>
                </label>
                <input
                    id='inquiry-phone'
                    name='phone'
                    type='tel'
                    inputMode='tel'
                    autoComplete='tel'
                    required
                    value={form.phone}
                    onChange={update('phone')}
                    aria-invalid={Boolean(errors.phone)}
                    aria-describedby={errors.phone ? 'inquiry-phone-error' : undefined}
                    placeholder='+91 98765 43210'
                    className={cn(inputClasses(Boolean(errors.phone)), 'h-12')}
                />
                {errors.phone && <p id='inquiry-phone-error' className='mt-2 text-sm text-destructive'>{errors.phone}</p>}
            </div>

            <div>
                <label htmlFor='inquiry-email' className={labelClasses}>
                    Email
                </label>
                <input
                    id='inquiry-email'
                    name='email'
                    type='email'
                    autoComplete='email'
                    value={form.email}
                    onChange={update('email')}
                    aria-invalid={Boolean(errors.email)}
                    aria-describedby={errors.email ? 'inquiry-email-error' : undefined}
                    placeholder='you@example.com'
                    className={cn(inputClasses(Boolean(errors.email)), 'h-12')}
                />
                {errors.email && <p id='inquiry-email-error' className='mt-2 text-sm text-destructive'>{errors.email}</p>}
            </div>

            <div>
                <label htmlFor='inquiry-eventType' className={labelClasses}>
                    Type of event
                </label>
                <div className='relative'>
                    <select
                        id='inquiry-eventType'
                        name='eventType'
                        value={form.eventType}
                        onChange={update('eventType')}
                        className={selectClasses}
                    >
                        <option value=''>Choose an event type</option>
                        {services.map((service) => (
                            <option key={service.id} value={service.title} data-mdlfy-cms={`services:${service.id}:title`}>{service.title}</option>
                        ))}
                        <option value={OTHER_EVENT}>{OTHER_EVENT}</option>
                    </select>
                    <SelectChevron />
                </div>
            </div>

            <div>
                <label htmlFor='inquiry-eventDate' className={labelClasses}>
                    Event date
                </label>
                <input
                    id='inquiry-eventDate'
                    name='eventDate'
                    type='date'
                    min={minDate}
                    value={form.eventDate}
                    onChange={update('eventDate')}
                    aria-invalid={Boolean(errors.eventDate)}
                    aria-describedby={errors.eventDate ? 'inquiry-eventDate-error' : undefined}
                    className={cn(inputClasses(Boolean(errors.eventDate)), 'h-12')}
                />
                {errors.eventDate && <p id='inquiry-eventDate-error' className='mt-2 text-sm text-destructive'>{errors.eventDate}</p>}
            </div>

            <div>
                <label htmlFor='inquiry-city' className={labelClasses}>
                    City or venue
                </label>
                <input
                    id='inquiry-city'
                    name='city'
                    type='text'
                    autoComplete='address-level2'
                    value={form.city}
                    onChange={update('city')}
                    placeholder='Ahmedabad, Udaipur, Mumbai...'
                    className={cn(inputClasses(false), 'h-12')}
                />
            </div>

            <div>
                <label htmlFor='inquiry-guests' className={labelClasses}>
                    Expected guests
                </label>
                <div className='relative'>
                    <select
                        id='inquiry-guests'
                        name='guests'
                        value={form.guests}
                        onChange={update('guests')}
                        className={selectClasses}
                    >
                        <option value=''>Select a range</option>
                        {guestOptions.map((option) => (
                            <option key={option} value={option}>{option}</option>
                        ))}
                    </select>
                    <SelectChevron />
                </div>
            </div>

            <div className='sm:col-span-2'>
                <label htmlFor='inquiry-budget' className={labelClasses}>
                    Approximate budget
                </label>
                <div className='relative'>
                    <select
                        id='inquiry-budget'
                        name='budget'
                        value={form.budget}
                        onChange={update('budget')}
                        className={selectClasses}
                    >
                        <option value=''>Select a budget range (optional)</option>
                        {budgetOptions.map((option) => (
                            <option key={option} value={option}>{option}</option>
                        ))}
                    </select>
                    <SelectChevron />
                </div>
            </div>

            <div className='sm:col-span-2'>
                <label htmlFor='inquiry-message' className={labelClasses}>
                    Tell us about your event
                </label>
                <textarea
                    id='inquiry-message'
                    name='message'
                    rows={5}
                    value={form.message}
                    onChange={update('message')}
                    placeholder='Theme, number of functions, special requests, anything that helps us plan.'
                    className={cn(inputClasses(false), 'resize-y py-3 leading-relaxed')}
                />
            </div>

            <div aria-hidden='true' className='absolute -left-[9999px] top-0 h-px w-px overflow-hidden'>
                <label htmlFor='inquiry-company'>Company</label>
                <input
                    id='inquiry-company'
                    name='company'
                    type='text'
                    tabIndex={-1}
                    autoComplete='off'
                    value={form.company}
                    onChange={update('company')}
                />
            </div>

            {status === 'error' && serverMessage && (
                <div role='alert' className='flex items-start gap-3 rounded-2xl border border-destructive/30 bg-destructive/5 p-4 text-sm text-ink sm:col-span-2'>
                    <CircleAlert className='mt-0.5 size-4 shrink-0 text-destructive' aria-hidden='true' />
                    <p>
                        {serverMessage}
                        {' '}
                        <a href={contactInfo.phoneHref} className='font-semibold text-crimson underline underline-offset-4'>
                            {contactInfo.phoneDisplay}
                        </a>
                    </p>
                </div>
            )}

            <div className='flex flex-col gap-3 pt-2 sm:col-span-2 sm:flex-row sm:items-center'>
                <button type='submit' disabled={submitting} className={buttonClasses('primary', 'lg', 'sm:min-w-52')}>
                    {submitting
                        ? <LoaderCircle className='size-4 animate-spin' aria-hidden='true' />
                        : <Send className='size-4' aria-hidden='true' />}
                    {submitting ? 'Sending...' : 'Send inquiry'}
                </button>

                <a
                    href={whatsappLink(whatsappSummary(form))}
                    target='_blank'
                    rel='noopener noreferrer'
                    className={buttonClasses('outline-dark', 'lg')}
                >
                    <WhatsAppIcon className='size-4 text-whatsapp' />
                    Send via WhatsApp instead
                </a>
            </div>

            <p id='inquiry-note' className='text-xs leading-relaxed text-muted-foreground sm:col-span-2'>
                Fields marked * are required. We only use your details to reply to this inquiry.
            </p>
        </form>
    )
}
